import { useEffect } from 'react';
import { Subscription, fromEvent, startWith } from 'rxjs';
import { toast } from '@/components/cms/ui/sonner';
import { offline } from '@/infrastructure/api/v1/client';
import { uploadRefused$ } from '@/infrastructure/api/v1/uploads';
import { serviceMonitor, SignalStrength } from './ServiceMonitor';

const ServiceMonitorBoot = () => {
  useEffect(() => {
    serviceMonitor.start();
    const subscriptions = new Subscription();
    // Saves made offline are sent as soon as the device is back online
    subscriptions.add(serviceMonitor.networkStrength$.subscribe(network => offline.setOnline(network !== SignalStrength.None)));
    // Saves go in the background all the time; only ones that were held up (offline, or the server failing) are told once through
    subscriptions.add(offline.synced$.subscribe(({ saved, refused, offline: madeOffline, reread }) => {
      if (saved > 0 && madeOffline) toast.success(saved === 1 ? 'Back online. Your change is saved.' : `Back online. Your ${saved} changes are saved.`, { id: 'offline-synced' });
      else if (saved > 0 && reread) toast.success(saved === 1 ? 'Your change is saved.' : `Your ${saved} changes are saved.`, { id: 'offline-synced' });
      const made = madeOffline ? ' made offline' : '';
      if (refused > 0) toast.error(refused === 1 ? `A change${made} couldn't be saved. The editor now shows what is saved.` : `${refused} changes${made} couldn't be saved. The editor now shows what is saved.`);
    }));
    // An upload storage refused for good is never tried again, so the person hears of it once
    subscriptions.add(uploadRefused$.subscribe(({ name, problem }) => {
      console.warn(`[uploads] ${name} was refused with ${problem.status} ${problem.code}`);
      toast.error(`A file couldn't be uploaded. ${problem.title}`, { id: 'upload-refused' });
    }));
    // Closing the tab with saves still waiting asks first
    let waiting = 0;
    subscriptions.add(offline.waiting$.pipe(startWith(0)).subscribe(count => (waiting = count)));
    subscriptions.add(fromEvent<BeforeUnloadEvent>(window, 'beforeunload').subscribe(event => {
      if (waiting === 0) return;
      event.preventDefault();
      // index.html shows its splash as an editor page goes; a leave cancelled at this prompt keeps the page, so it is hidden again
      setTimeout(() => document.getElementById('initial-splash')?.classList.add('hidden'), 0);
    }));
    return () => {
      subscriptions.unsubscribe();
      serviceMonitor.stop();
    };
  }, []);

  return null;
};

export default ServiceMonitorBoot;
